import type { Router } from '@angular/router';
import { DRIVE_CONTEXT_KEY, type DriveConversationElement, type RecentActivityEntry } from '@mtm/drive/core';
import type { MtmDriveExplorerComponent } from '@mtm/drive/components';
import { driveSelectionContextState } from './drive-context-state.utils';

interface RecentActivityContextMinter {
  create(state: Record<string, string | boolean>): Promise<string>;
}

export interface RecentActivityNavigationHandoff {
  prepare(contextUuid: string): void;
  cancel(): void;
}

function openRoutedAiConversation(
  drive: MtmDriveExplorerComponent,
  entry: RecentActivityEntry,
  elementUuid: string,
  expectedContextUuid: string | undefined
): boolean {
  if (entry.channelKind !== 'AI' || expectedContextUuid === undefined) return false;

  drive.openAiThread(
    entry.messageUuid,
    {
      elementType: entry.anchorKind === 'FILE' ? 'FILE_ALIAS' : entry.anchorKind,
      elementUuid,
    },
    entry.channelUuid,
    expectedContextUuid
  );
  return true;
}

/** Builds a detached Drive address while preserving context owned by the host. */
export function recentActivityContextState(
  currentState: Readonly<Record<string, string | boolean>>,
  entry: RecentActivityEntry
): Record<string, string | boolean> | null {
  if (!entry.elementUuid || entry.anchorKind === 'SURFACE_VIEW') {
    return null;
  }

  const state: Record<string, string | boolean> = driveSelectionContextState(
    currentState,
    entry.anchorKind === 'FILE' ? { fileAliasUuid: entry.elementUuid } : { directoryUuid: entry.elementUuid }
  );
  state[DRIVE_CONTEXT_KEY.sidebarTab] = 'comments';

  if (entry.channelKind === 'AI') {
    state[DRIVE_CONTEXT_KEY.feedPill] = 'ai';
  }

  if (entry.aiState === 'DRAFT') {
    state[DRIVE_CONTEXT_KEY.draftMessage] = entry.messageUuid;
  }

  if (entry.aiState !== 'DRAFT') {
    if (entry.channelKind === 'GENERAL') {
      if (entry.parentMessageUuid) {
        state[DRIVE_CONTEXT_KEY.threadRoot] = entry.parentMessageUuid;
      }
      state[DRIVE_CONTEXT_KEY.focusedMessage] = entry.messageUuid;
    } else {
      // The recent-AI read does not yet return parentMessageUuid, so messageUuid
      // may be either a root prompt or a follow-up. Never stamp that ambiguity as
      // a thread root: the live activation below resolves the real root and Drive
      // then mirrors it back into context.
      state[DRIVE_CONTEXT_KEY.focusedMessage] = entry.messageUuid;
    }
  }

  return state;
}

/**
 * Navigates through the shared view context, the sole Drive address.
 */
export async function navigateToRecentActivity(
  router: Router,
  contextMinter: RecentActivityContextMinter,
  currentState: Readonly<Record<string, string | boolean>>,
  entry: RecentActivityEntry,
  target: readonly string[] | null,
  explorer?: () => MtmDriveExplorerComponent | undefined,
  handoff?: RecentActivityNavigationHandoff
): Promise<void> {
  // A mounted known anchor is a live command. Its feed reports the final
  // resolved thread to context sync; replaying a detached context concurrently
  // could overwrite that root after the command has already completed.
  const mountedExplorer = target === null ? explorer?.() : undefined;
  if (mountedExplorer !== undefined && entry.anchorKind !== 'SURFACE_VIEW') {
    openRecentActivityInExplorer(() => mountedExplorer, entry);
    return;
  }

  const state: Record<string, string | boolean> | null = recentActivityContextState(currentState, entry);
  const ctxId: string | null = state ? await contextMinter.create(state) : entry.ctxId;
  if (!ctxId) {
    return;
  }

  handoff?.prepare(ctxId);
  let navigated: boolean;
  try {
    navigated = await router.navigate(target ?? [], {
      queryParams: { ctx: ctxId },
      ...(target === null ? { queryParamsHandling: 'merge' as const } : {}),
    });
  } catch (error: unknown) {
    handoff?.cancel();
    throw error;
  }
  if (!navigated) {
    handoff?.cancel();
    return;
  }

  if (explorer !== undefined) openRecentActivityInExplorer(explorer, entry, ctxId);
}

/**
 * Completes a context navigation inside the mounted explorer. Context owns the
 * project/element address; this command supplies the transient channel UUID and
 * lets Drive resolve an AI follow-up's exact thread root.
 */
export function openRecentActivityInExplorer(
  explorer: () => MtmDriveExplorerComponent | undefined,
  entry: RecentActivityEntry,
  expectedContextUuid?: string
): void {
  const drive = explorer();
  if (drive === undefined || !entry.elementUuid) return;

  if (entry.aiState === 'DRAFT') {
    if (entry.anchorKind === 'SURFACE_VIEW') return;
    drive.openDraft(entry.messageUuid, {
      elementType: entry.anchorKind === 'FILE' ? 'FILE_ALIAS' : entry.anchorKind,
      elementUuid: entry.elementUuid,
    });
    return;
  }

  if (entry.anchorKind === 'SURFACE_VIEW') {
    // A saved context is the only authoritative route to a compare view: the
    // recent row does not carry its parent directory, and router completion does
    // not mean asynchronous context hydration has finished. Never guess the
    // parent from the explorer's possibly stale current directory. Drive waits
    // for the named surface's feed to register before opening the exact channel.
    if (entry.channelKind === 'AI') {
      drive.openAiThread(
        entry.messageUuid,
        { elementType: 'SURFACE_VIEW', elementUuid: entry.elementUuid },
        entry.channelUuid,
        expectedContextUuid ?? entry.ctxId ?? undefined
      );
    }
    return;
  }

  if (openRoutedAiConversation(drive, entry, entry.elementUuid, expectedContextUuid)) return;

  let element: DriveConversationElement;
  if (entry.anchorKind === 'FILE') {
    element = {
      kind: 'FILE_ALIAS',
      uuid: entry.elementUuid,
      parentDirectoryUuid: null,
    };
  } else {
    element = { kind: 'DIRECTORY', uuid: entry.elementUuid };
  }

  drive.openConversation({
    channelKind: entry.channelKind === 'AI' ? 'ai' : 'comment',
    channelUuid: entry.channelUuid,
    // General rows know whether they are roots or replies. For AI rows null is
    // deliberately "unknown"; Drive resolves parent-or-self with getMessage.
    threadRootUuid: entry.channelKind === 'GENERAL' ? entry.parentMessageUuid : null,
    messageUuid: entry.messageUuid,
    element,
  });
}
